import { find, map } from "lodash";
import { IIntakePetModel, IModel, Model, IntakePetModel } from ".";

export interface IIntakeModel extends IModel {
    intakeId: number;
    intakePublicId: string;
    clientId: number | undefined;
    clinicId: number;

    firstName: string;
    lastName: string;
    address: string;
    address2: string;
    zipCode: string;
    city: string;
    county: string;
    stateId: number;
    stateAbbrev: string;
    cellPhone: string;
    alternatePhone: string;
    emailAddress: string;

    notificationSentDate: Date | undefined;
    responseCompleteDate: Date | undefined;
    responseLinkSource: string;
    firstOpenDate: Date | undefined;
    firstOpenLinkSource: string;
    wasPriorCustomer: boolean;
    storeCode: string;
    storeAddress: string;
    storeCity: string;
    storeStateAbbreviation: string;
    storeZipcode: string;
    storePhone: string;
    storePartnerName: string;
    storeBrandName: string;
    hasVisits: boolean;
    hasCompletedVisits: boolean;
    isClinicCancelled: boolean;
    pets: IIntakePetModel[];
}

export class IntakeModel extends Model implements Record<keyof IIntakeModel, any> {
    intakeId: number;
    intakePublicId: string;
    clientId: number | undefined;
    clinicId: number;

    firstName: string;
    lastName: string;
    address: string;
    address2: string;
    zipCode: string;
    city: string;
    county: string;
    stateId: number;
    stateAbbrev: string;
    cellPhone: string;
    alternatePhone: string;
    emailAddress: string;
    notificationSentDate: Date | undefined;
    responseCompleteDate: Date | undefined;
    responseLinkSource: string;
    firstOpenDate: Date | undefined;
    firstOpenLinkSource: string;
    wasPriorCustomer: boolean;
    storeCode: string;
    storeAddress: string;
    storeCity: string;
    storeStateAbbreviation: string;
    storeZipcode: string;
    storePhone: string;
    storePartnerName: string;
    storeBrandName: string;
    hasVisits: boolean;
    hasCompletedVisits: boolean;
    isClinicCancelled: boolean;
    pets: IntakePetModel[] = [];

    constructor(params: Partial<IIntakeModel> = {}) {
        super(params);
        const {
            intakeId = 0,
            intakePublicId = '',
            clientId = 0,
            clinicId = 0,
            firstName = '',
            lastName = '',
            address = '',
            address2 = '',
            zipCode = '',
            city = '',
            county = '',
            stateId = 0,
            stateAbbrev = '',
            cellPhone = '',
            alternatePhone = '',
            emailAddress = '',
            notificationSentDate = undefined,
            responseCompleteDate = undefined,
            responseLinkSource = '',
            firstOpenDate = undefined,
            firstOpenLinkSource = '',
            wasPriorCustomer = false,
            storeCode = '',
            storeAddress = '',
            storeCity = '',
            storeStateAbbreviation = '',
            storeZipcode = '',
            storePhone = '',
            storePartnerName = '',
            storeBrandName = '',
            hasVisits = false,
            hasCompletedVisits = false,
            isClinicCancelled = false,
            pets = [] as Array<IIntakePetModel>,
        } = params;

        this.intakeId = intakeId;
        this.intakePublicId = intakePublicId;
        this.clientId = clientId;
        this.clinicId = clinicId;
        this.firstName = firstName;
        this.lastName = lastName;
        this.address = address;
        this.address2 = address2;
        this.zipCode = zipCode;
        this.city = city;
        this.county = county;
        this.stateId = stateId;
        this.stateAbbrev = stateAbbrev;
        this.cellPhone = cellPhone;
        this.alternatePhone = alternatePhone;
        this.emailAddress = emailAddress;
        this.notificationSentDate = notificationSentDate;
        this.responseCompleteDate = responseCompleteDate;
        this.responseLinkSource = responseLinkSource;
        this.firstOpenDate = firstOpenDate;
        this.firstOpenLinkSource = firstOpenLinkSource;
        this.wasPriorCustomer = wasPriorCustomer;
        this.storeCode = storeCode;
        this.storeAddress = storeAddress;
        this.storeCity = storeCity;
        this.storeStateAbbreviation = storeStateAbbreviation;
        this.storeZipcode = storeZipcode;
        this.storePhone = storePhone;
        this.storePartnerName = storePartnerName;
        this.storeBrandName = storeBrandName;
        this.hasVisits = hasVisits;
        this.hasCompletedVisits = hasCompletedVisits;
        this.isClinicCancelled = isClinicCancelled;
        map(pets, (pet) => { this.pets.push(new IntakePetModel(pet)); });
    }


    get expectedFullVisitDate() {
        return (this.pets.length > 0 && this.pets[0].expectedFullVisitDate) ? this.pets[0].expectedFullVisitDate : undefined;
    }

    get visitDate() {
        return (this.pets.length > 0 && this.pets[0].expectedVisitDate) ? this.pets[0].expectedVisitDate : 'Today';
    }

    get visitTimes() {
        return (this.pets.length > 0) ? map(this.pets, 'expectedVisitTime').join(', ') : '';
    }

    get visitDateAndTimes() {
        return (this.visitTimes) ? this.visitDate + ' - ' + this.visitTimes : this.visitDate;
    }

    get isIntakeCompleted() {
        if (!this.responseCompleteDate)
            return false;

        const incompletePets = find(this.pets, (pet) => {
            return pet.responseCompleteDate === undefined;
        });

        return (incompletePets);
    }

    get isReadOnly() {
        return this.hasCompletedVisits;
    }

    get targetRebookingReservation() {
        if (this.pets.length > 0) {
            const firstPetPassSourcedIntakePet = _.find(this.pets, (intakePet) => {
                return intakePet.reservation && (intakePet.reservation.source === 'petpass');
            }) as IntakePetModel | undefined;

            if (firstPetPassSourcedIntakePet) {
                return firstPetPassSourcedIntakePet.reservation;
            } else {
                const firstIntakePetWithReservation = _.find(this.pets, (intakePet) => {
                    return intakePet.reservation !== undefined;
                });

                if (!_.isUndefined(firstIntakePetWithReservation)) {
                    return firstIntakePetWithReservation.reservation;
                } else {
                    return undefined;
                }
            }
        } else {
            return undefined;
        }
    }
}
